import darkSvg from './dark.svg';
import lightSvg from './light.svg';
import { useState, type MouseEvent } from 'react';
import { formatUnits } from 'viem';
import { ArrowUpDown } from 'lucide-react';
import { useProfile } from '@/routes/Providers/ProfileProvider';
import { track } from '@/shared/utils/track';
import { formatPrice } from '@/modules/format';
import { useSanBalance } from '@/modules/use-san-balance';
import { useWalletActionGuard } from '@/shared/utils/useWalletActionGuard';
import { useColorTheme } from '@/ColorThemeProvider';
import SmartLink from '@/shared/ui/SmartLink';
import ScoreArenaLogo from '@/shared/ui/ScoreArenaLogo';
import Notifications from './Notifications';
import Button from '@/shared/ui/Button';
import { useGatedNav } from '@/shared/utils/useGatedNav';
import { zHeaderNotifications, zMobileTopBar } from '@/shared/constants/zIdxs';
import { useIsMobile } from '@/modules/use-window-width';
import { useNewOpenMarketsCount } from '@/shared/utils/useNewOpenMarketsCount';
import { useRunWhenReady } from '@/modules/use-run-when-ready';
import SwapModal from './SwapModal';
import ProfileMenu from './ProfileMenu';
import ScoreOgModal from './ScoreOgModal';
import { isDemoAccount } from '@/modules/demo-account';

const NAV_LABELS = {
  markets: 'Markets',
  arena: 'Arena',
  community: 'Community',
};

// `label` is the analytics and onboarding-anchor constant; the shown text
// is the nav key's translation.
const links = [
  { key: 'markets', label: 'Markets', to: '/markets' },
  { key: 'arena', label: 'Arena', to: '/arena' },
  { key: 'community', label: 'Community', to: '/community' },
] as const;

const NAV_LINK_CLASS =
  'flex h-8 items-center justify-center px-m rounded-xs text-body-md font-medium text-gray whitespace-nowrap aria-[current]:text-purple-default hover:text-purple-hover';

export default function Header() {
  const { profile, login, isAuthReady } = useProfile();
  const { pending: signInPending, run: runSignIn } = useRunWhenReady(
    isAuthReady,
    login,
  );
  const { switchColorTheme, isDark } = useColorTheme();
  const isMobile = useIsMobile();
  const [swapOpen, setSwapOpen] = useState(false);
  const { onNavClick, gateEl } = useGatedNav('header');
  const [scoreOgOpen, setScoreOgOpen] = useState(false);
  const newMarketsCount = useNewOpenMarketsCount();
  const { balance: sanBalance } = useSanBalance(profile?.address ?? undefined);
  const { guardWalletAction, walletGuardModal } = useWalletActionGuard(
    profile?.address ?? null,
  );

  const loginButtonEl = !profile && (
    <Button
      variant="primary"
      size={!isMobile ? 'lg' : 'sm'}
      loading={signInPending}
      onClick={() => {
        track('market_signin_clicked', { source: 'header' });
        runSignIn();
      }}
    >
      Sign In
    </Button>
  );

  const logoEl = (
    <SmartLink className="flex items-center" to="/" aria-label="Score Arena">
      <ScoreArenaLogo />
    </SmartLink>
  );

  const iconUrl = JSON.stringify(!isDark ? darkSvg : lightSvg);
  const maskValue = `url(${iconUrl}) no-repeat center / contain`;
  const colorThemeButtonEl = (
    <button
      type="button"
      className="btn-reset flex size-8 items-center justify-center rounded-xs"
      onClick={switchColorTheme}
    >
      <div
        className="size-5 bg-gray-text"
        style={{ mask: maskValue, WebkitMask: maskValue }}
      />
    </button>
  );

  return (
    <div
      className="bg-background-secondary max-md:sticky max-md:top-0 max-md:rounded-b-sm max-md:drop-shadow-[0px_4px_2px_rgba(0,0,0,0.05)]"
      style={!isMobile ? undefined : { zIndex: zMobileTopBar }}
    >
      <div className="mx-auto max-w-306">
        <div className="flex items-center justify-between py-xl max-md:px-l max-md:py-s">
          {!isMobile ?
            <>
              <div className="flex items-center">
                <div className="flex w-52 items-center">{logoEl}</div>
                <div className="flex items-center gap-xl">
                  {links.map(({ key, label, to: src }) => {
                    const isMarkets = src === '/markets';
                    const showBadge = isMarkets && newMarketsCount > 0;
                    return (
                      <SmartLink
                        key={label}
                        className={NAV_LINK_CLASS}
                        navLink
                        data-onboarding={`nav-${label.toLowerCase()}`}
                        onClick={(e: MouseEvent) => onNavClick(e, label)}
                        to={src}
                      >
                        {NAV_LABELS[key]}
                        {showBadge && (
                          <span className="ml-xs text-purple-default">
                            · {newMarketsCount} new
                          </span>
                        )}
                      </SmartLink>
                    );
                  })}
                  {profile != null && isDemoAccount(profile) && (
                    <button
                      type="button"
                      className="btn-reset flex h-8 items-center justify-center rounded-xs px-m text-body-md font-medium whitespace-nowrap text-purple-default hover:text-purple-hover"
                      onClick={() => {
                        track('score_og_modal_shown');
                        setScoreOgOpen(true);
                      }}
                    >
                      🔥 Join as OG
                    </button>
                  )}
                  <SmartLink
                    className={NAV_LINK_CLASS}
                    navLink
                    onClick={(e: MouseEvent) => onNavClick(e, 'Help')}
                    to="/help"
                  >
                    Help
                  </SmartLink>
                </div>
              </div>
              <div className="flex items-center gap-2xl">
                {/* Wallet-bound affordance: hidden for walletless (SSO)
                    accounts — the modal itself is gated the same way. */}
                {profile?.address != null && (
                  <SanSwapPill
                    balance={sanBalance}
                    onClick={() => {
                      if (!guardWalletAction()) return;
                      track('swap_modal_opened', { source: 'header' });
                      setSwapOpen(true);
                    }}
                  />
                )}
                <div className="flex items-center gap-l">
                  {colorThemeButtonEl}
                  {profile && (
                    <div style={{ zIndex: zHeaderNotifications }}>
                      <Notifications />
                    </div>
                  )}
                  {profile && <ProfileMenu />}
                  {loginButtonEl}
                </div>
              </div>
            </>
          : <>
              {logoEl}
              {/* The ≤767 bar carries no nav, Help or profile entry: the
                  bottom nav pill owns navigation and the profile tab, /help
                  is reachable by URL and from the desktop header. Logged
                  out (the public market and KOL pages) the bell's slot holds the
                  Sign In button — no logged-out bar is designed. */}
              <div className="flex items-center gap-s">
                {colorThemeButtonEl}
                {profile && <Notifications />}
                {loginButtonEl}
              </div>
            </>
          }
        </div>
      </div>
      {swapOpen && profile?.address != null && (
        <SwapModal
          address={profile.address}
          onClose={() => setSwapOpen(false)}
        />
      )}
      {gateEl}
      {scoreOgOpen && <ScoreOgModal onClose={() => setScoreOgOpen(false)} />}
      {walletGuardModal}
    </div>
  );
}

// Desktop-only: the mobile treatment is undesigned, not deliberately
// excluded.
function SanSwapPill({
  balance,
  onClick,
}: {
  balance: bigint | undefined;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="btn-reset flex h-8 items-center gap-s rounded-sm bg-background-main px-m text-body-md font-medium whitespace-nowrap"
    >
      <span className="text-gray">
        Available SAN:{' '}
        {balance === undefined ?
          '—'
        : formatPrice(Number(formatUnits(balance, 18)))}
      </span>
      <span className="text-gray-text">Swap</span>
      <ArrowUpDown size={16} className="text-gray-text" />
    </button>
  );
}
